import React, { useState, useEffect } from "react";
import SectionContainer from "../../components/SectionContainer";
import Reveal from "../../components/Reveal";
import InteractiveTechChart from "../../components/InteractiveTechChart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faEye,
  faHeart,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";

const AboutUsSection = () => {
  const [activeCard, setActiveCard] = useState(0);

  const missionCards = [
    {
      icon: faRocket,
      title: "Our Mission",
      description:
        "To revolutionize how organizations operate using cutting-edge technologies, pushing the limits of innovation to transform industries.",
      color: "from-[#E5C558] to-[#d4b54d]",
      bgColor: "from-[#E5C558]/10 to-[#d4b54d]/10",
    },
    {
      icon: faEye,
      title: "Our Vision",
      description:
        "Creating a future where intelligent automation and digital maturity unlock unprecedented opportunities for businesses worldwide.",
      color: "from-blue-500 to-indigo-600",
      bgColor: "from-blue-500/10 to-indigo-600/10",
    },
    {
      icon: faHeart,
      title: "Our Values",
      description:
        "Built with intelligence, agility, and vision. We believe in ethical innovation, sustainable solutions, and empowering our clients' success.",
      color: "from-pink-500 to-rose-600",
      bgColor: "from-pink-500/10 to-rose-600/10",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % missionCards.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [missionCards.length]);

  return (
    <SectionContainer id="about-us" variant="alt">
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="about-pattern"
                width="25"
                height="25"
                patternUnits="userSpaceOnUse"
              >
                <rect width="1" height="1" fill="currentColor" />
                <rect x="12" y="12" width="1" height="1" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#about-pattern)" />
          </svg>
        </div>

        {/* Header Section */}
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/10 rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/20">
            <FontAwesomeIcon icon={faLightbulb} className="mr-2" />
            Innovation Excellence
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
            About{" "}
            <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
              GeniusNexa
            </span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Transforming industries through cutting-edge technology and
            innovative solutions that drive digital transformation.
          </p>
        </div>

        {/* Main Content Section */}
        <div className="flex flex-col lg:flex-row items-center gap-16 mb-20 relative z-10">
          <Reveal className="w-full lg:w-1/2">
            <InteractiveTechChart />
          </Reveal>

          <Reveal className="w-full lg:w-1/2">
            <div className="space-y-8">
              <div className="max-w-none text-gray-800 dark:text-white">
                <p className="text-base sm:text-lg text-gray-600 dark:text-white leading-relaxed mb-6">
                  We push the limits of innovation to transform industries. Our
                  comprehensive approach combines artificial intelligence,
                  blockchain technology, and advanced automation to create
                  solutions that enhance efficiency and accelerate digital
                  maturity.
                </p>
                <p className="text-base sm:text-lg text-gray-600 dark:text-white leading-relaxed mb-6">
                  From startups to enterprise organizations, we partner with
                  visionary leaders to build the future of technology. Our
                  platforms don't just solve today's challenges—they anticipate
                  tomorrow's opportunities.
                </p>
                <p className="text-base sm:text-lg text-gray-600 dark:text-white leading-relaxed">
                  Discover how GeniusNexa is shaping tomorrow and creating
                  opportunities once unimaginable. The future is here—built with
                  intelligence, agility, and vision.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Mission, Vision, Values Cards */}
        <div className="mb-20 relative z-10">
          <Reveal>
            <h3 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
              Our Foundation
            </h3>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {missionCards.map((card, index) => (
              <Reveal key={index} delay={0.1 + index * 0.1}>
                <div
                  className={`group relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer overflow-hidden h-full flex flex-col ${
                    activeCard === index
                      ? "ring-2 ring-[#E5C558] ring-opacity-50"
                      : ""
                  }`}
                  onClick={() => setActiveCard(index)}
                >
                  {/* Background Gradient */}
                  <div
                    className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${card.bgColor} rounded-full transform translate-x-16 -translate-y-16 group-hover:opacity-30 transition-opacity duration-500`}
                  ></div>

                  {/* Icon */}
                  <div
                    className={`w-16 h-16 bg-gradient-to-br ${card.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg relative z-10`}
                  >
                    <FontAwesomeIcon
                      icon={card.icon}
                      className="text-white text-2xl"
                    />
                  </div>

                  {/* Content */}
                  <div className="relative z-10 flex-grow">
                    <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {card.title}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {card.description}
                    </p>
                  </div>

                  {/* Bottom Border */}
                  <div
                    className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${card.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-b-3xl`}
                  ></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default AboutUsSection;
